import React, { useState } from 'react';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  UserPlus, 
  X, 
  CheckCircle, 
  Crown, 
  Star, 
  Sparkles, 
  Copy, 
  Mail,
  Loader2
} from 'lucide-react';

// Tier Badge Component for styled tier labels
const TierBadge = ({ tier }) => {
  const tierConfig = {
    founding_member: {
      label: 'Founding Member',
      icon: Crown,
      bgColor: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20',
      textColor: 'text-amber-400',
      borderColor: 'border-amber-500/30',
    },
    premium: {
      label: 'Premium',
      icon: Star,
      bgColor: 'bg-gradient-to-r from-orange-500/20 to-pink-500/20',
      textColor: 'text-orange-400',
      borderColor: 'border-orange-500/30',
    },
    enterprise: {
      label: 'Enterprise',
      icon: Sparkles,
      bgColor: 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20',
      textColor: 'text-purple-400',
      borderColor: 'border-purple-500/30',
    },
    free: {
      label: 'Free',
      icon: null,
      bgColor: 'bg-[#FDF2F8]/10',
      textColor: 'text-[#FDF2F8]/60',
      borderColor: 'border-transparent',
    },
  };

  const config = tierConfig[tier] || tierConfig.free;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.label}
    </span>
  );
};

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    referral_source: '',
    referral_other: '',
    membership_tier: 'free',
    send_welcome_email: true,
    password: '',
    // UTM tracking
    utm_source: '',
    utm_medium: '',
    utm_campaign: ''
  });
  const [createdUserResult, setCreatedUserResult] = useState(null);

  // Referral source options
  const referralSources = [
    'Google Search',
    'Facebook',
    'Instagram',
    'TikTok',
    'YouTube',
    'Friend/Referral',
    'Podcast',
    'Blog/Article',
    'Facebook Ad',
    'Google Ad',
    'Email',
    'Other'
  ];

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email) {
      toast.error('Email is required');
      return;
    }
    
    try {
      setCreateUserLoading(true);
      const res = await axios.post(`${API}/admin/users/create`, {
        email: newUser.email,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        phone_number: newUser.phone_number || undefined,
        referral_source: newUser.referral_source === 'Other' ? newUser.referral_other : newUser.referral_source || undefined,
        membership_tier: newUser.membership_tier,
        send_welcome_email: newUser.send_welcome_email,
        password: newUser.password || undefined,
        // UTM tracking
        utm_source: newUser.utm_source || undefined,
        utm_medium: newUser.utm_medium || undefined,
        utm_campaign: newUser.utm_campaign || undefined
      });
      
      setCreatedUserResult(res.data);
      toast.success(`User created successfully with ${res.data.membership_tier} tier`);
      
      // Notify parent to refresh user lists
      if (onUserCreated) {
        onUserCreated(res.data);
      }
      
      // Reset form but keep modal open to show result
      setNewUser({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        referral_source: '',
        referral_other: '',
        membership_tier: 'free',
        send_welcome_email: true,
        password: '',
        utm_source: '',
        utm_medium: '',
        utm_campaign: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleClose = () => {
    setCreatedUserResult(null);
    setNewUser({
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      referral_source: '',
      referral_other: '',
      membership_tier: 'free',
      send_welcome_email: true,
      password: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={handleClose}
    >
      <div 
        className="relative max-w-lg w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        style={{
          background: 'linear-gradient(135deg, rgba(223, 18, 131, 0.1) 0%, rgba(45, 6, 62, 0.95) 30%)',
          border: '1px solid rgba(223, 18, 131, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="create-user-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white"
          data-testid="create-user-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Add New Member</h3>
            <p className="text-sm text-white/60">Create a user account with any tier</p>
          </div>
        </div>

        {/* Success Result */}
        {createdUserResult && (
          <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">User Created Successfully!</span>
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-white/80"><strong>Email:</strong> {createdUserResult.email}</p>
              <p className="text-white/80"><strong>Name:</strong> {createdUserResult.name}</p>
              <p className="text-white/80"><strong>Tier:</strong> <TierBadge tier={createdUserResult.membership_tier} /></p>
              {createdUserResult.temporary_password && (
                <div className="mt-3 p-3 rounded-lg bg-black/30">
                  <p className="text-white/60 text-xs mb-1">Temporary Password (save this!):</p>
                  <div className="flex items-center gap-2">
                    <code className="text-[#0FFFE2] font-mono">{createdUserResult.temporary_password}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdUserResult.temporary_password);
                        toast.success('Password copied to clipboard');
                      }}
                      className="text-white/50 hover:text-white"
                      data-testid="copy-password-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              {createdUserResult.email_sent && (
                <p className="text-green-400 text-xs mt-2">
                  <Mail className="w-3 h-3 inline mr-1" />
                  Welcome email sent with login credentials
                </p>
              )}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleCreateUser} className="space-y-4">
          {/* Name Fields - FIRST */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-white/70 mb-1">First Name *</label>
              <input
                type="text"
                value={newUser.first_name}
                onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                placeholder="First name"
                data-testid="create-user-first-name"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1">Last Name</label>
              <input
                type="text"
                value={newUser.last_name}
                onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                placeholder="Last name"
                data-testid="create-user-last-name"
              />
            </div>
          </div>

          {/* Email - SECOND */}
          <div>
            <label className="block text-sm text-white/70 mb-1">Email Address *</label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
              placeholder="user@example.com"
              required
              data-testid="create-user-email"
            />
          </div>

          {/* Phone Number - THIRD */}
          <div>
            <label className="block text-sm text-white/70 mb-1">Phone Number</label>
            <input
              type="tel"
              value={newUser.phone_number}
              onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
              className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
              placeholder="+1 (555) 123-4567"
              data-testid="create-user-phone"
            />
          </div>

          {/* How They Found Us */}
          <div>
            <label className="block text-sm text-white/70 mb-1">How did they find us?</label>
            <select
              value={newUser.referral_source}
              onChange={(e) => setNewUser({...newUser, referral_source: e.target.value})}
              className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white focus:outline-none focus:border-[#df1283]"
              data-testid="create-user-referral-source"
            >
              <option value="">Select source...</option>
              {referralSources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>

          {/* Other Referral Source */}
          {newUser.referral_source === 'Other' && (
            <div>
              <label className="block text-sm text-white/70 mb-1">Please specify</label>
              <input
                type="text"
                value={newUser.referral_other}
                onChange={(e) => setNewUser({...newUser, referral_other: e.target.value})}
                className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                placeholder="Where did they find us?"
                data-testid="create-user-referral-other"
              />
            </div>
          )}

          {/* Membership Tier */}
          <div>
            <label className="block text-sm text-white/70 mb-1">Membership Tier</label>
            <select
              value={newUser.membership_tier}
              onChange={(e) => setNewUser({...newUser, membership_tier: e.target.value})}
              className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white focus:outline-none focus:border-[#df1283]"
              data-testid="create-user-tier"
            >
              <option value="free">Free</option>
              <option value="founding_member">Founding Member</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          {/* UTM Tracking (Collapsible) */}
          <div className="border border-white/10 rounded-lg p-3">
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer text-sm text-white/70 hover:text-white">
                <span>UTM Tracking (Optional)</span>
                <span className="text-xs text-white/40">Click to expand</span>
              </summary>
              <div className="mt-3 space-y-3 pt-3 border-t border-white/10">
                <div>
                  <label className="block text-xs text-white/50 mb-1">UTM Source</label>
                  <input
                    type="text"
                    value={newUser.utm_source}
                    onChange={(e) => setNewUser({...newUser, utm_source: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                    placeholder="e.g., facebook, google, instagram"
                    data-testid="create-user-utm-source"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">UTM Medium</label>
                  <input
                    type="text"
                    value={newUser.utm_medium}
                    onChange={(e) => setNewUser({...newUser, utm_medium: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                    placeholder="e.g., cpc, email, social"
                    data-testid="create-user-utm-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">UTM Campaign</label>
                  <input
                    type="text"
                    value={newUser.utm_campaign}
                    onChange={(e) => setNewUser({...newUser, utm_campaign: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                    placeholder="e.g., summer_promo, launch_2026"
                    data-testid="create-user-utm-campaign"
                  />
                </div>
              </div>
            </details>
          </div>

          {/* Password (optional) */}
          <div>
            <label className="block text-sm text-white/70 mb-1">Password (optional)</label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
              placeholder="Leave blank to auto-generate"
              data-testid="create-user-password"
            />
            <p className="text-xs text-white/40 mt-1">If left blank, a random password will be generated</p>
          </div>

          {/* Send Welcome Email */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="send-welcome-email"
              checked={newUser.send_welcome_email}
              onChange={(e) => setNewUser({...newUser, send_welcome_email: e.target.checked})}
              className="w-4 h-4 rounded border-white/20 bg-black/30 text-[#df1283] focus:ring-[#df1283]"
              data-testid="create-user-send-email"
            />
            <label htmlFor="send-welcome-email" className="text-sm text-white/70">
              Send welcome email with login credentials
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              data-testid="create-user-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createUserLoading}
              className="flex-1 bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
              data-testid="create-user-submit"
            >
              {createUserLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </span>
              ) : (
                'Create Member'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
